import Button from 'tio-ui/components/button';
import { fn } from '@ember/helper';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { on } from '@ember/modifier';
import { PhCheckCircle, PhFile, PhArrowRight } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import embedPdf from 'tio-common/modifiers/embed-pdf';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import pageTitle from 'ember-page-title/helpers/page-title';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { PslfFormExtension as PslfForm } from 'tio-data/data/resources/pslf-form.ext';

interface S {
  Args: {
    model: {
      primaryForm: PslfForm;
      pastPeriodForms: PslfForm[];
      allForms: PslfForm[];
    };
  };
}

export default class AuthenticatedPslfFormSuccess extends Component<S> {
  @service declare intl: IntlService;
  @tracked pdfSrc = '';

  get allForms() {
    return this.args.model.allForms;
  }

  get hasMultipleForms() {
    return this.allForms.length > 1;
  }

  openPdfModal = (index: number) => {
    const form = this.allForms[index];
    const url = form?.attachments?.[0]?.signedUrl;
    this.pdfSrc = url || '';
  };

  closePdfModal = () => {
    this.pdfSrc = '';
  };

  get showPdf() {
    return !!this.pdfSrc;
  }

  get showManualApproverCopy() {
    const primaryForm = this.args.model.primaryForm;
    return (
      primaryForm.status === 'borrower_signed' &&
      !primaryForm.borrowerFields['borrower-print-and-mail'] &&
      !primaryForm.borrowerFields['borrower-prior-employer-closed']
    );
  }

  getFormLabel = (form: PslfForm, index: number): string => {
    if (index === 0) {
      if (form.isPriorEmployer) {
        return this.intl.t('pslf.success.prior_employer_form_label');
      }
      return this.intl.t('pslf.success.primary_form_label');
    }
    // For previous employment periods, show date range
    const startDate = form.employerFields?.['employment-start-date'];
    const endDate = form.employerFields?.['employment-end-date'];
    if (startDate && endDate) {
      const formattedStart = this.intl.formatDate(new Date(startDate), {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      });
      const formattedEnd = this.intl.formatDate(new Date(endDate), {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      });
      return this.intl.t('pslf.success.past_period_label', {
        startDate: formattedStart,
        endDate: formattedEnd,
      });
    }
    return this.intl.t('pslf.success.past_period_form_label', { index });
  };

  <template>
    {{pageTitle "PSLF | Success" prepend=false}}
    <VStack>
      <Heading @level={{1}}>{{t "pslf.default_full"}}</Heading>
      <Section>
        <:body>
          <HStack class="items-start">
            <PhCheckCircle @size="24" class="tio-pslf-success-icon flex-none" />
            <VStack class="items-start">
              <Heading @level={{2}}>
                {{#if this.hasMultipleForms}}
                  {{t "pslf.success.pslf_forms_submitted_successfully" count=this.allForms.length}}
                {{else}}
                  {{t "pslf.success.pslf_form_submitted_successfully"}}
                {{/if}}
              </Heading>
              <Text @tag="p" @variant="muted">
                {{#if this.hasMultipleForms}}
                  {{t "pslf.success.forms_successfully_signed"}}
                {{else if this.showManualApproverCopy}}
                  {{t "pslf.success.form_sent_to_employer"}}
                {{else}}
                  {{t "pslf.success.form_successfully_signed"}}
                {{/if}}
              </Text>
              <VStack>
                {{#each this.allForms as |form index|}}
                  {{#if form.attachments.length}}
                    <div>
                      <Button
                        @appearance="minimal"
                        @icon={{PhFile}}
                        @label={{this.getFormLabel form index}}
                        data-test-view-preview-button={{index}}
                        {{on "click" (fn this.openPdfModal index)}}
                      />
                    </div>
                  {{/if}}
                {{/each}}
              </VStack>
              <Button
                @appearance="minimal"
                @intent="primary"
                @icon={{PhArrowRight}}
                @label={{t "pslf.success.back_to_pslf_dashboard"}}
                data-test-back-to-dashboard-button
                {{on "click" (transitionTo "authenticated.pslf.dashboard")}}
              />
            </VStack>
          </HStack>
        </:body>
      </Section>
    </VStack>
    <Modal
      @isOpen={{this.showPdf}}
      @onClose={{this.closePdfModal}}
      @ariaLabel="PDF Preview"
      class="min-h-64"
      @size="full"
    >
      <:body>
        {{#if this.pdfSrc}}
          <div class="w-full h-full max-w-4xl mx-auto">
            <div {{embedPdf this.pdfSrc}}></div>
          </div>
        {{/if}}
      </:body>
      <:footer>
        <Footer @isRunning={{false}} @onSubmit={{this.closePdfModal}} @submitText="Close Preview" />
      </:footer>
    </Modal>
  </template>
}
